import { Children, isValidElement } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Canonical secondary-metadata strip for queue rows.
 *
 * Every dense queue row (Patches, Projects, Knowledge, Runs, Inbox,
 * Follow-ups) carries a small muted line under its title with 2–4
 * scannable facts: workspace · agent · time, or codebase · tags ·
 * guardrails, or sender · tenant. Before this primitive each page
 * picked its own font size, separator glyph, gap, and `text-fg-*`
 * tone — six different vocabularies for the same information tier.
 *
 *   <RowMeta>
 *     <RowMeta.Item icon={GitBranch}>main</RowMeta.Item>
 *     <RowMeta.Item icon={Tag}>auth, billing</RowMeta.Item>
 *     <RowMeta.Item>v3</RowMeta.Item>
 *     <RowMeta.Item><RelativeTime iso={row.updated_at}/></RowMeta.Item>
 *   </RowMeta>
 *
 * The strip auto-inserts ` · ` separators between siblings (the
 * single dot character we standardised on) so callers stop hand-
 * sprinkling `<span>·</span>` glyphs. Passing `null`/`undefined`
 * children is safe — empty items collapse without leaving stray
 * separators behind, so conditionally-rendered metadata (e.g. agent
 * name when set) doesn't produce ` ·  · `.
 *
 * Tier conventions:
 *   font:   text-[11px]
 *   colour: text-fg-dim (separators stay one step softer at text-fg-muted)
 *   icons:  10px (lucide-react), inherit-coloured
 *   gap:    1.5
 *
 * These are the operator-density defaults — fixed deliberately so
 * pages can't drift back into per-row variation. Pages that need a
 * brighter row (e.g. the timestamp column rendered standalone) reach
 * for `<RelativeTime>` directly without `<RowMeta>` wrapping.
 */

export type RowMetaProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * `inline` (default) — flex-wrap allowed, single horizontal strip.
   * Use everywhere; `flex-wrap` keeps narrow viewports + crowded rows
   * from horizontal-scrolling.
   */
  layout?: "inline";
};

export function RowMeta({ children, className, layout = "inline" }: RowMetaProps) {
  // Filter out falsy children so a conditional `cond && <Item>` doesn't
  // produce an extra separator. `Children.toArray` already drops `null`
  // / `undefined` / `false`, so we only need to discard empty strings
  // here — TypeScript's narrowed ReactChild type rules out the others.
  const items = Children.toArray(children).filter((c) => c !== "");
  if (items.length === 0) return null;
  return (
    <div
      className={cn(
        "flex items-center text-[11px] text-fg-dim gap-1.5",
        layout === "inline" && "flex-wrap",
        className,
      )}
    >
      {items.map((child, i) => (
        <span key={i} className="contents">
          {i > 0 && <span aria-hidden className="text-fg-muted">·</span>}
          {child}
        </span>
      ))}
    </div>
  );
}

export type RowMetaItemProps = {
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
  /** Truncate with the supplied tailwind max-width hint (e.g. "max-w-[180px]"). */
  truncate?: string;
};

function RowMetaItem({ icon: Icon, children, className, truncate }: RowMetaItemProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 min-w-0",
        truncate,
        className,
      )}
    >
      {Icon && <Icon size={10} className="shrink-0" aria-hidden />}
      <span className={cn(truncate && "truncate")}>{children}</span>
    </span>
  );
}

RowMeta.Item = RowMetaItem;
